/**
 * @copyright 2026 Fortee Uniforme Inc. All rights reserved.
 * @license PROPRIETARY - Unauthorized copying prohibited.
 * Protected under the Canadian Copyright Act (R.S.C., 1985, c. C-42).
 */

// fortee-touchup-ui.js - quick 260819-bb8. The DOM half of the "Touch up"
// surface, shared by BOTH builders exactly like fortee-logo-editor.js and
// fortee-logo-pipeline.js. The pixel maths is in assets/fortee-bg-touchup.js and
// the cutout maths is in assets/fortee-bg-retention.js; nothing in this file
// re-derives either.
//
// Usage - spread both into the Alpine component:
//     Object.assign(component, window.forteeTouchupState(), window.forteeTouchup());
//
// ADAPTER CONTRACT - the host must already carry the LOGO PIPELINE
// (window.forteeLogoPipeline), because this surface reuses its seams rather than
// growing its own:
//
//   _loadImageToCanvas(url, maxDim)   load at the PRINT cap, not the analysis cap
//   _pipelineArtwork(view)            the artwork being touched up
//   _applyBgRemoved(view, origUrl, filename, url)
//                                     THE WRITE PATH. Writes through
//                                     _pipelineWriteArtwork, re-records the
//                                     library row, re-crops, and regenerates
//                                     versions - the same path every other
//                                     artwork takes.
//   _t(key, fallback) / showToast(msg, ok)
//
// -----------------------------------------------------------------------------
// THREE THINGS THAT WILL BREAK THIS IF THEY ARE "SIMPLIFIED"
// -----------------------------------------------------------------------------
//
// 1. THE SAVED URL MUST BE https. A `data:` url looks perfect for the whole
//    session and then vanishes: _safeUrl (builder-shared-services.js:157) returns
//    null for anything but http(s), and both builders strip data urls before
//    persisting, so the customer's repair would silently not be in the cart. The
//    edited PNG is uploaded to Uploadcare and the hosted url is re-validated
//    before it is written. If the upload fails the PREVIOUS artwork stands, the
//    surface stays open so the edit is not lost, and the customer is told.
//
// 2. THE EDIT HAPPENS AT PRINT RESOLUTION. Both loads use PRINT_MAX_DIM (2400),
//    the same cap _unionBgCandidate uses. Editing on the 1200px analysis canvas
//    would quietly swap a full-resolution print file for a downscaled one, and
//    nobody would notice until production.
//
// 3. THE MODE VARIABLE IS THE ONLY SOURCE OF TRUTH FOR THE TOOL. In the
//    prototype the Magic button was hard-coded as pressed in the markup while
//    the JS still held a different default, so the wrong tool ran while the UI
//    looked right. Every button here binds :aria-pressed to _tuMode, so the
//    markup CANNOT disagree with the state - there is nothing to keep in sync.
//
// -----------------------------------------------------------------------------
// WHY THE BUFFERS ARE NOT COMPONENT STATE
// -----------------------------------------------------------------------------
// At the 2400px cap the working set is two RGBA frames plus a label map - tens
// of megabytes. Those live in the module-scoped `S` below, one instance per page
// (the same choice the editor makes with window.__ftb2FabricCanvas), so Alpine
// never walks them and closeTouchup() can drop the lot in one assignment.

(function () {
  'use strict';

  var PRINT_MAX_DIM = 2400;

  // Undo depth, and a hard ceiling on what undo may cost in memory. Patches are
  // rectangles (see diffRect), so a normal stroke costs a few kilobytes and the
  // ceiling only ever bites after several full-frame actions.
  var HISTORY_MAX = 24;
  var HISTORY_BYTES = 24 * 1024 * 1024;

  var MIN_SCALE = 0.15;
  var MAX_SCALE = 14;

  // The live editing session. Null whenever the surface is closed.
  var S = null;

  function engine() { return window.forteeBgTouchup || null; }
  function retention() { return window.forteeBgRetention || null; }

  function pubkey() {
    return (window.bundleData && window.bundleData.uploadcarePubkey) ||
      (window.forteeShared && window.forteeShared._uploadcarePubkey) || '';
  }

  window.forteeTouchupState = function forteeTouchupState() {
    return {
      _tuOpen: false,
      _tuView: null,            // the placement key being repaired
      _tuBusy: false,           // loading or saving - blocks input
      _tuBusyMsg: '',
      _tuReady: false,          // pixels are loaded and the tools are live
      // THE single source of truth for the active tool. Every button's pressed
      // state binds to this; see note 3 in the header.
      _tuMode: 'magic',         // magic | restore | erase | pan
      // SCREEN pixels, converted to source pixels at paint time. 40 sits just
      // under a thumb contact patch: big enough to fill a hole in one dab, small
      // enough to see what it is about to cover.
      _tuBrush: 40,
      _tuGhostOn: true,
      _tuGarment: 'check',      // check | black | white | grey | blue
      _tuCanFix: false,         // false on a photographic background (THE GATE)
      _tuCanMagic: false,
      _tuCanUndo: false,
      _tuRestored: 0,           // % of what was missing that has been painted back
      _tuPromoted: false,       // opened because the guard flagged this artwork
      _tuNote: ''               // one line of plain-language context
    };
  };

  window.forteeTouchup = function forteeTouchup() {
    return {

      // =====================================================================
      // Open / close
      // =====================================================================

      // Always available on any artwork; the card promotes it when the retention
      // guard flagged the result. `view` is a resolved placement key - the
      // callers do that resolution because the two builders key artwork
      // differently and only they know how.
      openTouchup: function (view) {
        var self = this;
        if (!view) return;
        var art = this._pipelineArtwork(view);
        // Writing artwork to a view that does not already hold some would create
        // a decoration nobody registered or paid for. This surface REPAIRS an
        // existing placement and never creates one - the placement picker is
        // still the only thing that adds decorations.
        if (!art || !(art.url || art.bgRemovedUrl || art.originalUrl)) {
          this.showToast(this._t('touchup_no_art', 'Add a logo first.'), false);
          return;
        }

        this._tuView = view;
        this._tuOpen = true;
        this._tuReady = false;
        this._tuBusy = true;
        this._tuBusyMsg = this._t('touchup_loading', 'Opening your artwork…');
        this._tuPromoted = !!(art.analysis && art.analysis.needsReview);
        this._tuNote = '';
        this._tuRestored = 0;
        this._tuCanUndo = false;

        this.$nextTick(function () { self._tuLoad(view, art); });
      },

      closeTouchup: function () {
        this._tuUnbind();
        S = null;                 // drops every large buffer in one assignment
        this._tuOpen = false;
        this._tuReady = false;
        this._tuBusy = false;
        this._tuView = null;
      },

      // =====================================================================
      // Load
      // =====================================================================

      // PRINT RESOLUTION on both loads. The original supplies the colour and the
      // frame; the bg-removed file supplies the starting mask. When removal
      // failed or was rejected there is no real cutout, so the mask starts fully
      // opaque and "Fix artwork" is what performs the keying (that is the case
      // this surface exists for).
      _tuLoad: function (view, art) {
        var self = this;
        var E = engine();
        var origUrl = art.originalUrl || art.url;
        var cutUrl = art.bgRemovedUrl || null;
        if (!E || !retention() || !origUrl) {
          self._tuFail(self._t('touchup_unavailable', 'Touch up is unavailable right now.'));
          return;
        }

        Promise.all([
          self._loadImageToCanvas(origUrl, PRINT_MAX_DIM),
          cutUrl ? self._loadImageToCanvas(cutUrl, PRINT_MAX_DIM) : Promise.resolve(null)
        ]).then(function (pair) {
          var orig = pair[0];
          var cut = pair[1];
          if (!orig) {
            self._tuFail(self._t('touchup_load_failed', 'Could not open this artwork.'));
            return;
          }
          if (self._tuView !== view) return;   // the customer moved on

          var W = orig.canvas.width, H = orig.canvas.height;
          var frame = orig.ctx.getImageData(0, 0, W, H);

          // The starting mask.
          var alpha;
          if (cut) {
            var ci = cut.ctx.getImageData(0, 0, cut.canvas.width, cut.canvas.height);
            alpha = E.sampleAlphaOnto(W, H, ci.width, ci.height, ci.data);
          } else {
            alpha = new Uint8ClampedArray(W * H);
            for (var k = 0; k < W * H; k++) alpha[k] = frame.data[k * 4 + 3];
          }
          var hasCutout = E.isRealCutout(alpha);

          var an = E.analyzeFrame(W, H, frame.data);

          S = {
            w: W, h: H, total: W * H,
            canvas: null, ctx: null,
            frame: frame,
            alpha: alpha,
            baseAlpha: new Uint8ClampedArray(alpha),
            hasCutout: hasCutout,
            contentSoft: an.contentSoft,
            ghost: an.ghost,
            canFix: an.canFix,
            // Magic needs a label map, which on a photographic background would
            // be one component covering the frame - useless and the largest
            // allocation in the file. Skip it there and offer the brush instead.
            comps: an.canFix ? E.buildComponents(W, H, an.contentSoft) : null,
            grabbed: {},
            history: [],
            historyBytes: 0,
            strokeBefore: null,
            startOpaque: E.opaqueCount(alpha),
            scale: 1, tx: 0, ty: 0,
            drawing: false, panning: false,
            lastX: 0, lastY: 0, lastClientX: 0, lastClientY: 0,
            pointers: {}, pointerCount: 0, pinchDist: 0,
            bound: null,
            filename: (art.filename || 'artwork.png'),
            origUrl: origUrl
          };

          self._tuCanFix = an.canFix;
          self._tuCanMagic = !!(S.comps && S.comps.compCount > 0);
          // Never leave the customer holding a tool that is switched off. Bound
          // here rather than in the markup so the button state cannot drift.
          self._tuMode = self._tuCanMagic ? 'magic' : 'restore';
          self._tuNote = an.canFix
            ? (hasCutout ? '' : self._t('touchup_note_nocut', 'Tap Fix artwork to cut the background out, then touch up what is left.'))
            : self._t('touchup_note_photo', 'This photo background has to be painted out by hand.');

          self._tuBusy = false;
          self._tuReady = true;
          self.$nextTick(function () { self._tuMount(); });
        }).catch(function (err) {
          console.warn('[FORTEE touchup] load failed:', err);
          self._tuFail(self._t('touchup_load_failed', 'Could not open this artwork.'));
        });
      },

      _tuFail: function (msg) {
        this._tuBusy = false;
        this._tuReady = false;
        this._tuOpen = false;
        this._tuView = null;
        S = null;
        this.showToast(msg, false);
      },

      // =====================================================================
      // Canvas mount + input
      // =====================================================================

      // Pointer and wheel listeners are attached NATIVELY rather than with
      // x-on. A pointermove fires dozens of times a second while painting, and
      // routing each one through an Alpine expression evaluation is the
      // difference between a brush that tracks the thumb and one that lags
      // behind it. wheel additionally needs passive:false to preventDefault.
      _tuMount: function () {
        var self = this;
        if (!S) return;
        var canvas = this.$refs.tuCanvas;
        var stage = this.$refs.tuStage;
        var viewport = this.$refs.tuViewport;
        if (!canvas || !stage || !viewport) return;

        canvas.width = S.w;
        canvas.height = S.h;
        viewport.style.width = S.w + 'px';
        viewport.style.height = S.h + 'px';
        S.canvas = canvas;
        S.ctx = canvas.getContext('2d', { willReadFrequently: true });

        this._tuRepaintAll();
        this.touchupFit();

        var b = {
          down: function (e) { self._tuDown(e); },
          move: function (e) { self._tuMove(e); },
          up: function (e) { self._tuUp(e); },
          leave: function () { self._tuHideRing(); },
          wheel: function (e) { self._tuWheel(e); },
          resize: function () { if (S) self.touchupFit(); }
        };
        S.bound = b;
        stage.addEventListener('pointerdown', b.down);
        stage.addEventListener('pointermove', b.move);
        stage.addEventListener('pointerup', b.up);
        stage.addEventListener('pointercancel', b.up);
        stage.addEventListener('pointerleave', b.leave);
        stage.addEventListener('wheel', b.wheel, { passive: false });
        window.addEventListener('resize', b.resize);
      },

      _tuUnbind: function () {
        if (!S || !S.bound) return;
        var stage = this.$refs && this.$refs.tuStage;
        var b = S.bound;
        if (stage) {
          stage.removeEventListener('pointerdown', b.down);
          stage.removeEventListener('pointermove', b.move);
          stage.removeEventListener('pointerup', b.up);
          stage.removeEventListener('pointercancel', b.up);
          stage.removeEventListener('pointerleave', b.leave);
          stage.removeEventListener('wheel', b.wheel);
        }
        window.removeEventListener('resize', b.resize);
        S.bound = null;
      },

      _tuToArt: function (clientX, clientY) {
        var r = S.canvas.getBoundingClientRect();
        return {
          x: (clientX - r.left) / r.width * S.w,
          y: (clientY - r.top) / r.height * S.h
        };
      },

      _tuDown: function (e) {
        if (!S || !S.ctx || this._tuBusy) return;
        // The zoom buttons and the readout live inside the stage so they float
        // over the artwork. Without this a tap on "+" also stamps the brush.
        if (e.target && e.target.closest && e.target.closest('.ft-tu-float')) return;

        try { this.$refs.tuStage.setPointerCapture(e.pointerId); } catch (err) { /* not fatal */ }
        if (!S.pointers[e.pointerId]) S.pointerCount++;
        S.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

        if (S.pointerCount === 2) {
          // Two fingers is always a pinch, never a stroke - and the stroke that
          // the first finger started has to be abandoned, not merged into it.
          this._tuEndStroke();
          S.drawing = false;
          var p = this._tuPointerList();
          S.pinchDist = Math.sqrt(Math.pow(p[0].x - p[1].x, 2) + Math.pow(p[0].y - p[1].y, 2));
          return;
        }
        if (this._tuMode === 'pan' || e.button === 1) {
          S.panning = true;
          S.lastClientX = e.clientX; S.lastClientY = e.clientY;
          return;
        }

        var p0 = this._tuToArt(e.clientX, e.clientY);
        this._tuBeginStroke();
        S.drawing = true;
        S.lastX = p0.x; S.lastY = p0.y;
        S.grabbed = {};
        this._tuPaintAt(p0.x, p0.y, null);
      },

      _tuMove: function (e) {
        if (!S || !S.ctx) return;
        if (S.pointers[e.pointerId]) S.pointers[e.pointerId] = { x: e.clientX, y: e.clientY };

        if (S.pointerCount === 2) {
          var p = this._tuPointerList();
          var d = Math.sqrt(Math.pow(p[0].x - p[1].x, 2) + Math.pow(p[0].y - p[1].y, 2));
          if (S.pinchDist > 0 && d > 0) {
            this._tuZoomAround(d / S.pinchDist, (p[0].x + p[1].x) / 2, (p[0].y + p[1].y) / 2);
          }
          S.pinchDist = d;
          return;
        }

        if (e.pointerType === 'mouse') this._tuShowRing(e.clientX, e.clientY);

        if (S.panning) {
          S.tx += e.clientX - S.lastClientX;
          S.ty += e.clientY - S.lastClientY;
          S.lastClientX = e.clientX; S.lastClientY = e.clientY;
          this._tuApplyTransform();
          return;
        }
        if (!S.drawing) return;
        var p1 = this._tuToArt(e.clientX, e.clientY);
        this._tuPaintAt(p1.x, p1.y, { x: S.lastX, y: S.lastY });
        S.lastX = p1.x; S.lastY = p1.y;
      },

      _tuUp: function (e) {
        if (!S) return;
        if (S.pointers[e.pointerId]) { delete S.pointers[e.pointerId]; S.pointerCount--; }
        if (S.pointerCount < 2) S.pinchDist = 0;
        if (S.drawing) { S.drawing = false; this._tuEndStroke(); this._tuUpdateReadout(); }
        if (S.panning) S.panning = false;
      },

      _tuPointerList: function () {
        var out = [];
        for (var k in S.pointers) {
          if (Object.prototype.hasOwnProperty.call(S.pointers, k)) out.push(S.pointers[k]);
        }
        return out;
      },

      // How many SOURCE pixels one on-screen pixel is worth right now.
      //
      // This is the difference between a tool that works on a phone and one that
      // does not. A 2400px print file fitted into a 350px stage puts ~7 source
      // pixels behind every screen pixel, so a brush measured in source pixels
      // would be a seventh of the size it looks - and Magic's 9px probe would
      // demand pixel-perfect aim from a gloved thumb. Both are measured on the
      // SCREEN and converted here, so the brush is the size of the ring the
      // customer can see and zooming in genuinely buys finer control.
      _tuSourcePerScreenPx: function () {
        if (!S || !S.canvas) return 1;
        var r = S.canvas.getBoundingClientRect();
        if (!r.width) return 1;
        return S.w / r.width;
      },

      _tuBrushRadius: function () {
        return Math.max(1, this._tuBrush * this._tuSourcePerScreenPx());
      },

      // Never below the engine's floor: zoomed right in, one screen pixel is
      // less than one source pixel and the probe would shrink to nothing.
      _tuMagicRadius: function () {
        var E = engine();
        return Math.max(E.MAGIC_RADIUS, 12 * this._tuSourcePerScreenPx());
      },

      // One dab or one segment, whichever the tool and the gesture call for.
      _tuPaintAt: function (x, y, from) {
        var E = engine();
        var rect;
        if (this._tuMode === 'magic') {
          if (!S.comps) return;
          var mr = this._tuMagicRadius();
          rect = from
            ? E.magicLine(S.comps, S.grabbed, S.alpha, S.contentSoft, S.w, S.h, from.x, from.y, x, y, mr)
            : E.magicAt(S.comps, S.grabbed, S.alpha, S.contentSoft, S.w, S.h, x, y, mr);
        } else {
          var target = this._tuMode === 'erase' ? 0 : 255;
          var br = this._tuBrushRadius();
          rect = from
            ? E.strokeLine(S.alpha, S.w, S.h, from.x, from.y, x, y, br, target)
            : E.stamp(S.alpha, S.w, S.h, x, y, br, target);
        }
        if (rect) this._tuBlit(rect);
      },

      // =====================================================================
      // Painting to screen
      // =====================================================================

      _tuBlit: function (rect) {
        if (!S || !S.ctx) return;
        var r = engine().compositeShown(S.frame.data, S.alpha, S.ghost, this._tuGhostOn, S.w, S.h, rect);
        if (r.x1 <= r.x0 || r.y1 <= r.y0) return;
        S.ctx.putImageData(S.frame, 0, 0, r.x0, r.y0, r.x1 - r.x0, r.y1 - r.y0);
      },

      // The readout is updated whether or not there is a canvas to paint on.
      // Tying it to the paint would make the number a function of whether the
      // surface happened to be mounted, which is exactly the kind of coupling
      // that makes a headless test lie about what the customer sees.
      _tuRepaintAll: function () {
        if (!S) return;
        this._tuUpdateReadout();
        if (!S.ctx) return;
        engine().compositeShown(S.frame.data, S.alpha, S.ghost, this._tuGhostOn, S.w, S.h, null);
        S.ctx.putImageData(S.frame, 0, 0);
      },

      // "Restored" is a share of what was MISSING when the surface opened, not a
      // share of the frame. A customer who paints back everything the engine ate
      // should read 100%, whatever fraction of the picture that was.
      _tuUpdateReadout: function () {
        if (!S) return;
        var opaque = engine().opaqueCount(S.alpha);
        var room = Math.max(1, S.total - S.startOpaque);
        var v = Math.round((opaque - S.startOpaque) / room * 100);
        this._tuRestored = Math.max(0, Math.min(100, v));
      },

      // =====================================================================
      // View
      // =====================================================================

      _tuApplyTransform: function () {
        var vp = this.$refs.tuViewport;
        if (!vp || !S) return;
        vp.style.transform = 'translate(' + S.tx + 'px,' + S.ty + 'px) scale(' + S.scale +
          ') translate(-50%,-50%)';
      },

      touchupFit: function () {
        if (!S) return;
        var stage = this.$refs.tuStage;
        if (!stage) return;
        var r = stage.getBoundingClientRect();
        var pad = 28;
        var s = Math.min((r.width - pad) / S.w, (r.height - pad) / S.h);
        S.scale = (isFinite(s) && s > 0) ? s : 1;
        S.tx = 0; S.ty = 0;
        this._tuApplyTransform();
      },

      _tuZoomAround: function (factor, cx, cy) {
        var next = Math.max(MIN_SCALE, Math.min(MAX_SCALE, S.scale * factor));
        var stage = this.$refs.tuStage;
        if (stage && cx != null) {
          var r = stage.getBoundingClientRect();
          var ox = cx - r.left - r.width / 2 - S.tx;
          var oy = cy - r.top - r.height / 2 - S.ty;
          S.tx -= ox * (next / S.scale - 1);
          S.ty -= oy * (next / S.scale - 1);
        }
        S.scale = next;
        this._tuApplyTransform();
      },

      touchupZoom: function (factor) {
        if (!S) return;
        var stage = this.$refs.tuStage;
        var r = stage ? stage.getBoundingClientRect() : null;
        this._tuZoomAround(factor, r ? r.left + r.width / 2 : null, r ? r.top + r.height / 2 : null);
      },

      _tuWheel: function (e) {
        if (!S) return;
        e.preventDefault();
        this._tuZoomAround(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
      },

      // The ring is drawn in screen pixels, and both radii are already screen
      // measures, so what the customer sees is exactly what will be painted.
      _tuShowRing: function (clientX, clientY) {
        var ring = this.$refs.tuRing;
        var stage = this.$refs.tuStage;
        if (!ring || !stage || !S || !S.canvas) return;
        var sr = stage.getBoundingClientRect();
        var perPx = this._tuSourcePerScreenPx();
        var srcR = this._tuMode === 'magic' ? this._tuMagicRadius() : this._tuBrushRadius();
        var size = (srcR / (perPx || 1)) * 2;
        ring.style.left = (clientX - sr.left) + 'px';
        ring.style.top = (clientY - sr.top) + 'px';
        ring.style.width = size + 'px';
        ring.style.height = size + 'px';
        ring.classList.add('is-on');
      },

      _tuHideRing: function () {
        var ring = this.$refs.tuRing;
        if (ring) ring.classList.remove('is-on');
      },

      // =====================================================================
      // Controls
      // =====================================================================

      touchupSetMode: function (m) {
        if (m === 'magic' && !this._tuCanMagic) return;
        this._tuMode = m;
      },

      touchupSetBrush: function (v) {
        var n = parseInt(v, 10);
        if (!isFinite(n)) return;
        this._tuBrush = Math.max(6, Math.min(160, n));
      },

      touchupSetGhost: function (on) {
        this._tuGhostOn = !!on;
        this._tuRepaintAll();
      },

      touchupSetGarment: function (g) { this._tuGarment = g; },

      // =====================================================================
      // Undo
      // =====================================================================

      _tuBeginStroke: function () {
        if (!S || S.strokeBefore) return;
        S.strokeBefore = new Uint8ClampedArray(S.alpha);
      },

      // Store only the rectangle the stroke actually changed. See diffRect in
      // fortee-bg-touchup.js for why whole-mask snapshots are not an option at
      // print resolution.
      _tuEndStroke: function () {
        if (!S || !S.strokeBefore) return;
        var E = engine();
        var rect = E.diffRect(S.strokeBefore, S.alpha, S.w, S.h);
        if (rect) {
          var patch = E.extractRect(S.strokeBefore, S.w, rect);
          S.history.push({ rect: rect, patch: patch });
          S.historyBytes += patch.length;
          while (S.history.length > HISTORY_MAX ||
                 (S.history.length > 1 && S.historyBytes > HISTORY_BYTES)) {
            S.historyBytes -= S.history.shift().patch.length;
          }
          this._tuCanUndo = true;
        }
        S.strokeBefore = null;
      },

      touchupUndo: function () {
        if (!S || !S.history.length) return;
        var h = S.history.pop();
        S.historyBytes -= h.patch.length;
        engine().applyRect(S.alpha, S.w, h.rect, h.patch);
        this._tuCanUndo = S.history.length > 0;
        this._tuRepaintAll();
      },

      // =====================================================================
      // Actions
      // =====================================================================

      // One tap. Reuses the SHIPPED union - window.forteeBgRetention
      // .unionWithContent - rather than re-deriving it, so a manual repair
      // produces byte-identical pixels to the automatic one and there is no seam
      // to see. THE GATE lives inside that function: on a photographic
      // background it declines, because unioning the content measurement back in
      // would restore the background itself.
      //
      // It REPLACES the mask rather than merging into it, and that is
      // deliberate. When removal failed or was rejected the live mask is fully
      // opaque, so merging would be a no-op and the one button that matters
      // would appear broken on exactly the artwork this surface exists for.
      // Undo covers the case where the customer had already painted.
      touchupFix: function () {
        if (!S || this._tuBusy) return;
        var R = retention();
        if (!R) return;

        // The candidate is the cutout we STARTED from - the engine's own answer.
        // When there was no real cutout, a 1x1 transparent frame is the honest
        // candidate: nearest-neighbour sampling spreads it over the whole frame,
        // so the union reduces to the content keying, computed by the shipped
        // function instead of by a second copy of it here.
        var cand;
        if (S.hasCutout) {
          var data = new Uint8ClampedArray(S.total * 4);
          for (var i = 0; i < S.total; i++) data[i * 4 + 3] = S.baseAlpha[i];
          cand = { w: S.w, h: S.h, data: data };
        } else {
          cand = { w: 1, h: 1, data: new Uint8ClampedArray(4) };
        }

        var u = R.unionWithContent({ w: S.w, h: S.h, data: S.frame.data }, cand);
        cand = null;
        if (!u.applied) {
          this.showToast(this._t('touchup_fix_declined',
            'This background is too detailed to cut out automatically — use the brush.'), false);
          return;
        }

        this._tuBeginStroke();
        for (var k = 0; k < S.total; k++) S.alpha[k] = u.data[k * 4 + 3];
        u = null;
        this._tuEndStroke();
        this._tuRepaintAll();
        this.showToast(this._t('touchup_fixed', 'Artwork restored — check it over.'), true);
      },

      // Back to the cutout the surface opened with.
      touchupReset: function () {
        if (!S) return;
        this._tuBeginStroke();
        S.alpha.set(S.baseAlpha);
        this._tuEndStroke();
        this._tuRepaintAll();
      },

      // The customer's file, background and all. The escape hatch when the
      // cutout is beyond repair and they would rather print what they uploaded.
      touchupShowAll: function () {
        if (!S) return;
        this._tuBeginStroke();
        S.alpha.fill(255);
        this._tuEndStroke();
        this._tuRepaintAll();
      },

      // =====================================================================
      // Save
      // =====================================================================

      // The edited PNG is uploaded, the hosted url is re-validated as https, and
      // the result goes through _applyBgRemoved - the SAME write path every
      // other artwork takes. That is what keeps pricing and placement
      // consistent: _applyBgRemoved writes through _pipelineWriteArtwork, merges
      // the library row, re-crops asynchronously and regenerates the colour
      // versions, and it writes to a view that ALREADY holds artwork, so no
      // decoration is created that nobody registered.
      //
      // It also clears the review flag as a side effect, because the object it
      // writes carries no `analysis` - which is correct: the customer just fixed
      // the thing that was flagged.
      touchupSave: function () {
        var self = this;
        if (!S || this._tuBusy) return;
        var view = this._tuView;
        var E = engine();

        this._tuBusy = true;
        this._tuBusyMsg = this._t('touchup_saving', 'Saving your artwork…');

        var dataUrl;
        try {
          // The saved frame is built from `alpha`, the TRUE mask. The ghost only
          // ever exists in the display buffer, so it cannot reach this file -
          // scripts/test-bg-touchup.cjs asserts that directly.
          var out = document.createElement('canvas');
          out.width = S.w; out.height = S.h;
          var octx = out.getContext('2d');
          var img = octx.createImageData(S.w, S.h);
          var src = S.frame.data;
          for (var i = 0; i < S.total; i++) {
            var o = i * 4;
            img.data[o] = src[o];
            img.data[o + 1] = src[o + 1];
            img.data[o + 2] = src[o + 2];
            img.data[o + 3] = S.alpha[i];
          }
          octx.putImageData(img, 0, 0);
          dataUrl = out.toDataURL('image/png');
        } catch (e) {
          this._tuBusy = false;
          this.showToast(this._t('touchup_save_failed',
            'Could not save — your artwork is unchanged. Please try again.'), false);
          return;
        }

        var key = pubkey();
        var upload = (key && window.forteeShared && window.forteeShared.uploadToUploadcare)
          ? window.forteeShared.uploadToUploadcare(dataUrl, key)
          : Promise.resolve(null);

        upload.then(function (hosted) {
          // A data: url would look perfect all session and then vanish from the
          // cart, because _safeUrl and both builders strip it before persisting.
          // Nothing but an https url is allowed past this line.
          if (typeof hosted !== 'string' || !/^https:\/\//i.test(hosted)) {
            throw new Error('upload did not return an https url');
          }
          var art = self._pipelineArtwork(view);
          var origUrl = (art && (art.originalUrl || art.url)) || S.origUrl;
          self._applyBgRemoved(view, origUrl, S.filename, hosted);
          self._tuBusy = false;
          self.closeTouchup();
          self.showToast(self._t('touchup_saved', 'Artwork updated.'), true);
        }).catch(function (err) {
          console.warn('[FORTEE touchup] save failed; keeping the previous artwork:', err);
          // The previous artwork stands and the surface STAYS OPEN, so the
          // customer's work is still on screen and they can try again. Losing an
          // edit silently is the one outcome this must never produce.
          self._tuBusy = false;
          self.showToast(self._t('touchup_save_failed',
            'Could not save — your artwork is unchanged. Please try again.'), false);
        });
      }
    };
  };
})();
